// src/pages/HomePage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  FaChartLine, 
  FaUsers, 
  FaShieldAlt, 
  FaClipboardList,
  FaCopy,
  FaCheckCircle 
} from 'react-icons/fa';

const HomePage = () => {
  const [organizationName, setOrganizationName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [copiedSurvey, setCopiedSurvey] = useState(false);
  const [copiedResults, setCopiedResults] = useState(false);
  const navigate = useNavigate();

  const generateSurveyId = () => {
    return Math.random().toString(36).substr(2, 8).toUpperCase();
  };

  const handleCreateSurvey = async (e) => {
    e.preventDefault();
    
    if (!organizationName.trim()) {
      alert('يرجى إدخال اسم المنظمة');
      return;
    }

    setLoading(true);
    try {
      const surveyId = generateSurveyId();
      const surveyDoc = {
        id: surveyId,
        organizationName: organizationName.trim(),
        email: email.trim(),
        createdAt: new Date(),
        responses: []
      };

      await addDoc(collection(db, 'surveys'), surveyDoc);
      
      const baseUrl = window.location.origin;
      setSurveyData({
        ...surveyDoc,
        surveyLink: `${baseUrl}/survey/${surveyId}`,
        resultsLink: `${baseUrl}/results/${surveyId}`
      });

    } catch (error) {
      console.error('Error creating survey:', error);
      alert('حدث خطأ في إنشاء الاستبيان. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'survey') {
      setCopiedSurvey(true);
      setTimeout(() => setCopiedSurvey(false), 2000);
    } else {
      setCopiedResults(true);
      setTimeout(() => setCopiedResults(false), 2000);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <div className="text-center mb-12 slide-in">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          قِس سعادة موظفيك بكل سهولة
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          أداة لقياس مؤشر السعادة الوظيفية في منظمتك
        </p>
        <p className="text-lg text-gray-500 mt-2">
          بناءً على أحدث الأبحاث العلمية
        </p>
      </div>

      {!surveyData ? (
        <>
          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="text-center hover-card bg-white p-6 rounded-lg shadow">
              <FaChartLine className="text-4xl text-primary-green mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">تحليل متكامل</h3>
              <p className="text-gray-600 text-sm">5 محاور رئيسية و35 مؤشر</p>
            </div>
            <div className="text-center hover-card bg-white p-6 rounded-lg shadow">
              <FaUsers className="text-4xl text-primary-gold mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">سهل الاستخدام</h3>
              <p className="text-gray-600 text-sm">10-15 دقيقة فقط للإكمال</p>
            </div>
            <div className="text-center hover-card bg-white p-6 rounded-lg shadow">
              <FaShieldAlt className="text-4xl text-primary-green mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">خصوصية تامة</h3>
              <p className="text-gray-600 text-sm">استجابات مجهولة الهوية</p>
            </div>
          </div>

          {/* Create Survey Form */}
          <div className="max-w-lg mx-auto bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
              ابدأ الآن - مجاناً
            </h2>
            <form onSubmit={handleCreateSurvey}>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  اسم المنظمة *
                </label>
                <input
                  type="text"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                  placeholder="مؤثرون"
                  required
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  البريد الإلكتروني (اختياري)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                  placeholder="abusuhaib4all@gmail.com"
                />
                <p className="text-sm text-gray-500 mt-1">
                  لن يُشارك بريدك مع أي جهة
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || !organizationName.trim()}
                className="w-full bg-primary-green hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="spinner ml-3"></div>
                    جاري الإنشاء...
                  </>
                ) : (
                  <>
                    <FaClipboardList className="ml-2" />
                    إنشاء الاستبيان
                  </>
                )}
              </button>
            </form>
          </div>
        </>
      ) : (
        /* Success State */
        <div className="max-w-2xl mx-auto">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6 text-center">
            <FaCheckCircle className="text-5xl text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-800 mb-2">
              تم إنشاء الاستبيان بنجاح!
            </h2>
            <p className="text-green-700">
              الآن يمكنك مشاركة رابط الاستبيان مع الموظفين
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 space-y-4">
            {/* Survey Link */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                رابط الاستبيان (للموظفين)
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={surveyData.surveyLink}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg bg-gray-50"
                  dir="ltr"
                />
                <button
                  onClick={() => copyToClipboard(surveyData.surveyLink, 'survey')}
                  className="px-4 py-2 bg-primary-green text-white rounded-l-lg hover:bg-green-700 transition-colors"
                >
                  {copiedSurvey ? <FaCheckCircle /> : <FaCopy />}
                </button>
              </div>
            </div>

            {/* Results Link */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                رابط النتائج (للإدارة)
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={surveyData.resultsLink}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg bg-gray-50"
                  dir="ltr"
                />
                <button
                  onClick={() => copyToClipboard(surveyData.resultsLink, 'results')}
                  className="px-4 py-2 bg-primary-gold text-white rounded-l-lg hover:bg-yellow-700 transition-colors"
                >
                  {copiedResults ? <FaCheckCircle /> : <FaCopy />}
                </button>
              </div>
            </div>

            <div className="pt-4 space-y-2 text-sm text-gray-600">
              <p>📊 رمز الاستبيان: <strong>{surveyData.id}</strong></p>
              <p>💡 نصيحة: احفظ روابط النتائج في مكان آمن</p>
            </div>

            <button
              onClick={() => {
                setSurveyData(null);
                setOrganizationName('');
                setEmail('');
              }}
              className="w-full mt-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              إنشاء استبيان جديد
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
